import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Edit, Trash2, Settings, HelpCircle } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSchools } from '@/hooks/useSchools'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useQuizzesRealtime } from '@/hooks/useQuizzesRealtime'
import QuizMappingDialog from './QuizMappingDialog'
import QuizQuestionsDialog from './QuizQuestionsDialog'

const AdminQuizzes = () => {
  const [quizzes, setQuizzes] = useState([])
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false)
  const [questionsDialogOpen, setQuestionsDialogOpen] = useState(false)
  const [editingQuiz, setEditingQuiz] = useState(null)
  const [selectedQuizForMapping, setSelectedQuizForMapping] = useState(null)
  const [selectedQuizForQuestions, setSelectedQuizForQuestions] = useState(null)
  const [activeTab, setActiveTab] = useState('global')
  const { toast } = useToast()
  const { schools, loading: schoolsLoading } = useSchools()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    module_id: '',
    school_id: ''
  })

  const fetchModules = async () => {
    try {
      const { data, error } = await supabase
        .from('learn_modules')
        .select('id, title')
        .order('title')

      if (error) throw error
      setModules(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching modules",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const fetchQuizzes = async () => {
    try {
      const { data, error } = await supabase
        .from('quizzes')
        .select(`
          *,
          learn_modules(title),
          schools (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      setQuizzes(data || [])
      console.log('Fetched quizzes in admin:', data?.length || 0)
    } catch (error: any) {
      toast({
        title: "Error fetching quizzes",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Filter quizzes based on active tab
  const filteredQuizzes = quizzes.filter((quiz: any) => {
    if (activeTab === 'global') {
      return !quiz.school_id
    } else {
      return quiz.school_id
    }
  })

  // Group school-specific quizzes by school
  const quizzesBySchool = filteredQuizzes.reduce((acc: any, quiz: any) => {
    if (quiz.school_id) {
      const schoolName = quiz.schools?.name || 'Unknown School'
      if (!acc[schoolName]) {
        acc[schoolName] = []
      }
      acc[schoolName].push(quiz)
    }
    return acc
  }, {})

  // Set up real-time updates for quizzes
  useQuizzesRealtime({
    onQuizzesChange: fetchQuizzes,
    onQuizAttemptsChange: () => {
      // Quiz attempts don't affect admin view, but we keep it for consistency
    }
  })

  useEffect(() => {
    fetchQuizzes()
    fetchModules()
  }, [])

  const getAvailableModules = () => {
    if (editingQuiz) {
      // When editing, include the current quiz's module
      return modules
    }
    
    // When creating new, exclude modules that already have quizzes for the same school
    // (Global quizzes: school_id is null, School-specific: check school_id)
    const selectedSchoolId = formData.school_id === 'global' ? null : formData.school_id || null
    const usedModuleIds = quizzes
      .filter((quiz: any) => {
        // Only exclude if same school (or both global)
        return (quiz.school_id === selectedSchoolId)
      })
      .map((quiz: any) => quiz.module_id)
    return modules.filter(module => !usedModuleIds.includes(module.id))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check for duplicate module assignment when creating new quiz (same school)
    if (!editingQuiz) {
      const selectedSchoolId = formData.school_id === 'global' ? null : formData.school_id || null
      const existingQuiz = quizzes.find((quiz: any) => 
        quiz.module_id === formData.module_id && quiz.school_id === selectedSchoolId
      )
      if (existingQuiz) {
        toast({
          title: "Module already has a quiz",
          description: "This module already has a quiz for the selected school. Please select a different module or school.",
          variant: "destructive",
        })
        return
      }
    }

    try {
      const quizData = {
        ...formData,
        school_id: formData.school_id === 'global' ? null : formData.school_id || null
      }

      if (editingQuiz) {
        const { error } = await supabase
          .from('quizzes')
          .update(quizData)
          .eq('id', editingQuiz.id)
        
        if (error) throw error
        toast({ title: "Quiz updated successfully" })
      } else {
        const { error } = await supabase
          .from('quizzes')
          .insert(quizData)
        
        if (error) throw error
        toast({ title: "Quiz created successfully" })
      }

      setDialogOpen(false)
      setEditingQuiz(null)
      setFormData({
        title: '',
        description: '',
        module_id: '',
        school_id: ''
      })
      // Real-time will handle the refresh
    } catch (error: any) {
      toast({
        title: "Error saving quiz",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEdit = (quiz: any) => {
    setEditingQuiz(quiz)
    setFormData({
      title: quiz.title,
      description: quiz.description || '',
      module_id: quiz.module_id,
      school_id: quiz.school_id || 'global'
    })
    setDialogOpen(true)
  }

  const handleDelete = async (quizId: string) => {
    try {
      const { error } = await supabase
        .from('quizzes')
        .delete()
        .eq('id', quizId)

      if (error) throw error
      toast({ title: "Quiz deleted successfully" })
      // Real-time will handle the refresh
    } catch (error: any) {
      toast({
        title: "Error deleting quiz",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleManageMappings = (quiz: any) => {
    setSelectedQuizForMapping(quiz)
    setMappingDialogOpen(true)
  }

  const handleManageQuestions = (quiz: any) => {
    setSelectedQuizForQuestions(quiz)
    setQuestionsDialogOpen(true)
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Quiz Management</CardTitle>
          <CardDescription>Loading quizzes...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const availableModules = getAvailableModules()

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Quiz Management</CardTitle>
            <CardDescription>Create and manage module quizzes and their answer mappings. Each module can only have one quiz.</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => {
                  setEditingQuiz(null)
                  setFormData({
                    title: '',
                    description: '',
                    module_id: '',
                    school_id: ''
                  })
                }}
                disabled={!editingQuiz && availableModules.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Quiz
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="module_id">Module</Label>
                  <Select 
                    value={formData.module_id} 
                    onValueChange={(value) => setFormData({ ...formData, module_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select module" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModules.map(module => (
                        <SelectItem key={module.id} value={module.id}>
                          {module.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!editingQuiz && availableModules.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      All modules already have quizzes assigned for this school.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school_id">Target University</Label>
                  <Select value={formData.school_id} onValueChange={(value) => setFormData({ ...formData, school_id: value, module_id: '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select target university (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (All Universities)</SelectItem>
                      {schools.map(school => (
                        <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" disabled={!formData.module_id}>
                  {editingQuiz ? 'Update Quiz' : 'Create Quiz'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="global">Global Quizzes</TabsTrigger>
            <TabsTrigger value="school-specific">School-Specific Quizzes</TabsTrigger>
          </TabsList>
          
          <TabsContent value="global" className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuizzes.map((quiz: any) => (
                  <TableRow key={quiz.id}>
                    <TableCell className="font-medium">{quiz.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{quiz.learn_modules?.title}</Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{quiz.description}</TableCell>
                    <TableCell>
                      {new Date(quiz.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(quiz)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleManageQuestions(quiz)}
                        >
                          <HelpCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleManageMappings(quiz)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(quiz.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredQuizzes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No global quizzes created yet. Click "Add Quiz" to create your first quiz.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="school-specific" className="mt-4">
            {Object.keys(quizzesBySchool).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No school-specific quizzes created yet. Click "Add Quiz" and select a university to create school-specific quizzes.
              </div>
            ) : (
              Object.entries(quizzesBySchool).map(([schoolName, schoolQuizzes]: [string, any]) => (
                <div key={schoolName} className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">{schoolName}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Module</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schoolQuizzes.map((quiz: any) => (
                        <TableRow key={quiz.id}>
                          <TableCell className="font-medium">{quiz.title}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{quiz.learn_modules?.title}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{quiz.description}</TableCell>
                          <TableCell>
                            {new Date(quiz.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(quiz)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleManageQuestions(quiz)}
                              >
                                <HelpCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleManageMappings(quiz)}
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(quiz.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <QuizMappingDialog
        quiz={selectedQuizForMapping}
        isOpen={mappingDialogOpen}
        onClose={() => {
          setMappingDialogOpen(false)
          setSelectedQuizForMapping(null)
        }}
      />

      <QuizQuestionsDialog
        quiz={selectedQuizForQuestions}
        isOpen={questionsDialogOpen}
        onClose={() => {
          setQuestionsDialogOpen(false)
          setSelectedQuizForQuestions(null)
        }}
      />
    </Card>
  )
}

export default AdminQuizzes
